import { isSameDay, max, min } from 'date-fns';
import dayjs from 'dayjs';
import { DayPickerProps, Matcher } from 'react-day-picker';

import { TLocale } from '@/config/locales';
import {
  getEffectiveMinimumStay,
  getRangeMinimumStay,
  TMinimumStayRequirement,
} from '@/utility/minimumStay';

// The rental's minimum trip length as stored on the listing. Aliased to the shared
// TMinimumStayRequirement so calendar callers and the listing-page badge use one shape.
export type TMinimumStay = TMinimumStayRequirement;

export { getEffectiveMinimumStay, getRangeMinimumStay };

// Why a date cannot start a minimum stay: 'checkout' when the very next day is blocked
// (the date can only be a checkout), 'minimum' when a later day inside the window is blocked.
// undefined when the date is a valid start.
type TStartRejection = 'checkout' | 'minimum' | undefined;

// Weekday names ordered Sunday → Saturday, to match react-day-picker's day index (0 = Sunday).
// Read off a fixed reference week: Jan 7–13, 2024 is one clean Sun→Saturday week (Jan 7 2024
// was a Sunday), so day-of-month maps directly to weekday with no month-boundary edge cases.
export const weekdaysForLocale = (
  locale: TLocale,
  weekday: 'long' | 'short' | 'narrow' | undefined,
): string[] => {
  const format = new Intl.DateTimeFormat(locale, { weekday });

  return [7, 8, 9, 10, 11, 12, 13].map(dayOfMonth => format.format(new Date(2024, 0, dayOfMonth)));
};

// Month names (January → December) for the given locale. The year is irrelevant to month
// names, so we hardcode one for a stable, deterministic result.
export const monthsForLocale = (locale: TLocale, type: 'long' | 'short'): string[] => {
  const formatter = new Intl.DateTimeFormat(locale, { month: type });

  return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(monthIndex =>
    formatter.format(new Date(2024, monthIndex)),
  );
};

// Whether a given day falls inside one of the disabled matchers. Availability is
// supplied as {from, to} ranges (and occasionally bare Dates), which is all we test here.
// Anything unrecognized (including null) is treated as "not a match" (false).
export const isDayWithinMatcher = (day: Date, matcher: unknown): boolean => {
  if (matcher instanceof Date) {
    return isSameDay(day, matcher);
  }
  if (matcher && typeof matcher === 'object') {
    const range = matcher as { from?: Date; to?: Date };
    if (range.from && range.to) {
      return !dayjs(day).isBefore(range.from, 'day') && !dayjs(day).isAfter(range.to, 'day');
    }
  }
  return false;
};

// Day-priced listings store the minimum in days; all calendar geometry works in nights.
export const getMinimumStayNights = (minimumStayRequirement?: TMinimumStay): number | undefined => {
  return minimumStayRequirement?.use_day_pricing
    ? minimumStayRequirement.duration - 1
    : minimumStayRequirement?.duration;
};

// Whether the minimum-stay window can be satisfied starting from `day`. Returns the reason it
// cannot (see TStartRejection) or undefined when the start is valid.
export const getStartRejection = (
  day: Date,
  minimumStayNights: number | undefined,
  isDayUnavailable: (day: Date) => boolean,
): TStartRejection => {
  for (let offset = 1; offset <= (minimumStayNights ?? 0); offset++) {
    if (isDayUnavailable(dayjs(day).add(offset, 'day').toDate())) {
      return offset === 1 ? 'checkout' : 'minimum';
    }
  }
};

// While only a start date is selected, restrict selection to the contiguous available block
// containing it: block every day at/after the first unavailable date following the start, and
// at/before the last unavailable date preceding it. This prevents picking a range (in either
// direction) that spans an unavailable interval. `from` itself is always available, so every
// disabled interval lies entirely before or entirely after it. Returns undefined when there is
// nothing to block (no start selected, a range is complete, or no bounding gaps exist).
export const createGapBlockMatcher = (
  dateFrom: Date | undefined,
  dateTo: Date | undefined,
  disabledDays: Matcher[],
): ((day: Date) => boolean) | undefined => {
  if (!dateFrom || dateTo) return undefined;

  let firstUnavailableAfter: Date | undefined;
  let lastUnavailableBefore: Date | undefined;

  disabledDays.forEach(matcher => {
    const start = matcher instanceof Date ? matcher : (matcher as { from?: Date }).from;
    const end = matcher instanceof Date ? matcher : (matcher as { to?: Date }).to;

    if (!start || !end) return;

    if (dayjs(start).isAfter(dateFrom, 'day')) {
      if (!firstUnavailableAfter || dayjs(start).isBefore(firstUnavailableAfter, 'day')) {
        firstUnavailableAfter = start;
      }
    } else if (dayjs(end).isBefore(dateFrom, 'day')) {
      if (!lastUnavailableBefore || dayjs(end).isAfter(lastUnavailableBefore, 'day')) {
        lastUnavailableBefore = end;
      }
    }
  });

  if (!firstUnavailableAfter && !lastUnavailableBefore) return undefined;

  return (day: Date) =>
    Boolean(firstUnavailableAfter && !dayjs(day).isBefore(firstUnavailableAfter, 'day')) ||
    Boolean(lastUnavailableBefore && !dayjs(day).isAfter(lastUnavailableBefore, 'day'));
};

// A day strictly between the start and the minimum-stay length can't be an end date — it would
// make the trip shorter than the minimum. Only active while just a start is selected; otherwise
// there is nothing to disable (returns `false`, i.e. an empty matcher).
const getMinimumStayDisabled = (dateFrom?: Date, dateTo?: Date, minimumNights?: number): Matcher =>
  minimumNights && dateFrom && !dateTo
    ? (day: Date) => {
        const daysDifference = Math.abs(dayjs(day).diff(dateFrom, 'day'));
        return daysDifference > 0 && daysDifference < minimumNights;
      }
    : false;

// Builds react-day-picker's `modifiers` map (the styling hooks for each cell) from the current
// selection and hover state. Handles both single-date and range pickers, and drives the
// start/end markers, the hovered interval preview, and the minimum-stay highlight/dimming.
export const getModifiers = ({
  range,
  dateFrom,
  dateTo,
  dayHover,
  maximumDays,
  minimumNights,
  isStartDisabled,
}: {
  range?: boolean;
  dateFrom?: Date;
  dateTo?: Date;
  dayHover?: Date;
  maximumDays?: number;
  minimumNights?: number;
  isStartDisabled?: (day: Date) => boolean;
}): DayPickerProps['modifiers'] => {
  if (!range) {
    const isHoveringSelectedDate = dayHover && dateFrom && isSameDay(dayHover, dateFrom);

    return {
      hover: isHoveringSelectedDate ? false : dayHover || false,
      single: dateFrom || false,
    };
  }

  // Seed the range modifiers. `start`/`end` mark the selected check-in/check-out. The two
  // "disabled" modifiers gray out cells at complementary stages of the selection:
  //   - minimumStayDisabled: while mid-selection (a check-in but no check-out) — end dates too
  //     close to the start to meet the minimum length. Hard-disabled (unclickable) via CSS.
  //   - minimumStartDisabled: whenever the next click starts a fresh selection (nothing picked,
  //     or a full range already picked — the next click resets to a new start) — start dates
  //     whose minimum window runs into an unavailable date. Only dimmed, still clickable so the
  //     click surfaces a tooltip. Off mid-selection, where the next click picks the end instead.
  const modifiers: DayPickerProps['modifiers'] = {
    start: dateFrom || false,
    end: dateTo || false,
    minimumStayDisabled: getMinimumStayDisabled(dateFrom, dateTo, minimumNights),
    minimumStartDisabled: !dateFrom || dateTo ? isStartDisabled || false : false,
  };

  // hover: the cell under the cursor, drawn as the hover ring — unless it's already the selected
  // start or end, which keep their own start/end markers instead.
  const isHoveringDateFrom = dayHover && dateFrom && isSameDay(dayHover, dateFrom);
  const isHoveringDateTo = dayHover && dateTo && isSameDay(dayHover, dateTo);

  modifiers.hover = !isHoveringDateFrom && !isHoveringDateTo ? dayHover || false : false;

  // Derived selection geometry. `selectedTo` is the effective end date — the committed check-out,
  // or the hovered cell while the user is still choosing one. The direction flags say where it
  // sits relative to the start, and `lastDayOfMinimum` is the earliest valid check-out.
  const selectedTo = dateTo || dayHover;
  const isSameAsDateFrom = selectedTo && dateFrom && isSameDay(selectedTo, dateFrom);
  const isAfterDateFrom = selectedTo && dateFrom && selectedTo >= dateFrom;
  const isBeforeDateFrom = selectedTo && dateFrom && selectedTo < dateFrom;
  const lastDayOfMinimum = minimumNights
    ? dayjs(dateFrom).add(minimumNights, 'day').toDate()
    : undefined;

  const isWithinMinimum =
    selectedTo && lastDayOfMinimum && selectedTo < lastDayOfMinimum && isAfterDateFrom;

  // interval: the filled band spanning the selection. Grows forward when the end is after the
  // start, or backward when the end is before it; capped to `maximumDays` from the start.
  if (dateFrom && selectedTo && !isSameAsDateFrom) {
    modifiers.interval = isAfterDateFrom
      ? {
          from: dateFrom,
          to:
            maximumDays && maximumDays > 0
              ? min([
                  selectedTo,
                  dayjs(dateFrom)
                    .add(maximumDays - 1, 'day')
                    .toDate(),
                ])
              : selectedTo,
        }
      : {
          from:
            maximumDays && maximumDays > 0
              ? max([
                  selectedTo,
                  dayjs(dateFrom)
                    .subtract(maximumDays - 1, 'day')
                    .toDate(),
                ])
              : selectedTo,
          to: dateFrom,
        };
  }

  // intervalFirst / intervalLast: rounded end-caps of the previewed band while only a start is
  // picked and the user is hovering an end. A forward hover only needs the trailing cap; a
  // backward hover caps both ends. Skipped while the hover is still inside the minimum window.
  if (!dateTo && dayHover && !isWithinMinimum) {
    if (isAfterDateFrom) {
      modifiers.intervalLast = dayHover;
    } else {
      modifiers.intervalLast = dateFrom || false;
      modifiers.intervalFirst = dayHover;
    }
  }

  // minimum: highlights the minimum-stay window forward from the selected start
  // (dateFrom → dateFrom + minimumNights); minimumFirst / minimumLast round its start/end caps.
  // A start whose forward window is blocked is rejected on click (it never becomes `dateFrom`),
  // so a selected start always has a clear forward window — no availability check is needed here.
  if (dateFrom && !dateTo && lastDayOfMinimum && !isBeforeDateFrom) {
    modifiers.minimum = { from: dateFrom, to: lastDayOfMinimum };

    if (!isAfterDateFrom && dayHover) {
      modifiers.minimumLast = dateFrom;
    } else {
      modifiers.minimumFirst = dateFrom;
    }

    if (!dayHover || dayHover < lastDayOfMinimum) {
      modifiers.minimumLast = lastDayOfMinimum;
    }
  }

  return modifiers;
};
